<script setup>
// Originally copied from ui/components/core/SimpleTooltip.vue
import { defineProps, ref, computed, watchEffect } from 'vue';


const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  position: {
    type: String,
    default: 'top', // Can be top, bottom, left, or right
  },
  trigger: {
    type: String,
    default: 'hover', // Can be hover or click
  },
  tooltipClasses: {
    type: String,
    default: '',
  },
  active: {
    type: Boolean,
    default: true,
  },
  bgColor: {
    type: String,
    default: 'tw-bg-blue-500',
  },
  zIndex: {
    type: Number,
    default: 50,
  },
});


// Allows us to accept position in a simple format (top) or the Buefy format (is-top)
const normalizedPosition = computed(() => {
  const pos = props.position;

  if (pos.startsWith('is-')) return pos.substring(3);
  return pos;
});

const visible = ref(false);
const display = ref('hidden');


/*
Normally we would handle this with Vue's built in transition component.
However, we want this component to be compatible with both Vue 2 and 3.
The transition component is handled differently in Vue 2 and 3 so this is a workaround.
*/
watchEffect(() => {
  if (visible.value) display.value = '';

  else {
    setTimeout(() => display.value = 'tw-hidden', 200);
  }
});


/*
The computed props below set the appropriate tailwind classes to position the tooltip
These are the intended classes for each position:

        side            shift         translate
--------------------------------------------------------
bottom  tw-top-full     tw-left-1/2  -tw-translate-x-1/2
top     tw-bottom-full  tw-left-1/2  -tw-translate-x-1/2
left    tw-right-full   tw-top-1/2   -tw-translate-y-1/2
right   tw-left-full    tw-top-1/2   -tw-translate-y-1/2
*/


const side = computed(() => {
  const sides = {
    bottom: 'top',
    top: 'bottom',
    left: 'right',
    right: 'left',
  };

  const pos = normalizedPosition.value;
  return `tw-${sides[pos]}-full`;
});


const shift = computed(() => {
  const pos = normalizedPosition.value;

  const side = ['top', 'bottom'].includes(pos) ? 'left' : 'top';
  return `tw-${side}-1/2`;
});


const translate = computed(() => {
  const pos = normalizedPosition.value;

  const dir = ['top', 'bottom'].includes(pos) ? 'x' : 'y';
  return `-tw-translate-${dir}-1/2`;
});


// Select the proper triangle direction based on the tooltip position
// TODO: Why aren't we using this?
/*
const direction = computed(() => {
  const directions = {
    bottom: 'up',
    top: 'down',
    left: 'right',
    right: 'left',
  };

  const pos = normalizedPosition.value;
  return directions[pos];
}); */


function handleMouseEnter() {
  if (props.trigger === 'hover' && props.active) visible.value = true;
}

function handleClick() {
  if (props.trigger === 'click' && props.active) visible.value = true;
}

function handleMouseLeave() {
  visible.value = false;
}
</script>


<template>
  <div
    class="tw-relative tw-inline-block"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleClick"
  >
    <slot />

    <div
      :class="[
        'tw-absolute tw-p-1.5 tw-pointer-events-none',
        `tw-z-${zIndex}`,
        side, shift, translate,
        display,
        visible ? 'tw-opacity-100' : 'tw-opacity-0',
        'tw-transition-opacity',
      ]"
    >
      <div
        :class="[
          'tw-px-2.5 tw-py-1.5 tw-rounded tw-shadow',
          label ? `${bgColor} tw-text-white tw-text-sm tw-font-normal tw-normal-case tw-tracking-normal` : '',
          label?.length > 50 ? 'tw-w-80' : 'tw-whitespace-nowrap',
          tooltipClasses,
        ]"
      >
        <slot name="content">
          {{ label }}
        </slot>
      </div>
    </div>
  </div>
</template>
